/**
 * Textes de consentement côté joueur (manette) : nom de l'établissement affiché
 * dans la case newsletter, et lien vers la politique de confidentialité.
 *
 * Pur (pas de Firestore, pas de stockage) → testable sans navigateur.
 */

/** Au-delà, le nom est tronqué : la case doit tenir sur un écran de 375 px. */
export const VENUE_NAME_MAX_LENGTH = 60;

/**
 * Nom d'établissement affichable, ou `null` si on doit retomber sur la formulation
 * neutre (« de cet établissement »). Le champ vient d'un document Firestore public
 * (`clients/{id}/public/userData.venueName`) : on ne présume rien de son type.
 */
export function cleanVenueName(raw: unknown): string | null {
    if (typeof raw !== 'string') return null;
    const name = raw.replace(/\s+/g, ' ').trim();
    if (!name) return null;
    // Par caractère (Array.from), pas par unité UTF-16 : ne pas couper un emoji en deux.
    const chars = Array.from(name);
    return chars.length > VENUE_NAME_MAX_LENGTH
        ? `${chars
              .slice(0, VENUE_NAME_MAX_LENGTH - 1)
              .join('')
              .trimEnd()}…`
        : name;
}

/**
 * Le nom commence-t-il par une voyelle ? Sert à l'élision française :
 * « offres d'Aéroport Lyon » et non « offres de Aéroport Lyon ».
 * Le h et le y sont volontairement exclus : « Hangar », « Hall », « Yard »,
 * « Yellow » donneraient « d'Hangar », « d'Yard » — pire que « de Hôtel ».
 */
export function startsWithVowel(name: string): boolean {
    return /^[aeiouàâäéèêëîïôöùûüœæ]/i.test(name);
}

/**
 * Politique de confidentialité dans la langue de l'appareil : seules /fr/ et /en/
 * existent sur le site (l'adresse sans préfixe renvoie 404).
 */
export function privacyPolicyUrl(lang: string | undefined | null): string {
    const code = (lang || '').substring(0, 2).toLowerCase();
    return `https://unboared.com/${code === 'fr' ? 'fr' : 'en'}/privacy-policy`;
}

/**
 * Ce qu'on sait du choix newsletter déjà enregistré pour CET établissement
 * (`clients/{id}/userData/{uid}.newsletterConsent`) :
 * - `pending` : lecture en cours ;
 * - `unknown` : la lecture a échoué — on ignore si le joueur avait dit oui ;
 * - `true` / `false` : valeur enregistrée (`false` aussi pour une fiche absente
 *   ou sans le champ : pour l'export, « pas de réponse » vaut « non »).
 */
export type SavedConsent = 'pending' | 'unknown' | boolean;

/** Lecture tolérante d'une fiche joueur : seul un `true` explicite vaut « oui ». */
export function savedConsentFromRecord(record: Record<string, unknown> | undefined): boolean {
    return record?.newsletterConsent === true;
}

/**
 * Que faire de `newsletterConsent` à l'enregistrement de la fiche.
 *
 * - On connaît la valeur enregistrée → la fiche est réécrite en entier, comme
 *   avant (les champs que l'établissement ne demande plus disparaissent), en
 *   reportant le choix du joueur — ou l'ancien s'il n'a pas touché la case.
 * - On ne la connaît pas (lecture en échec ou pas finie) → écriture FUSIONNÉE
 *   sans le champ, sauf si le joueur a touché la case : la case affichée
 *   « décochée » n'est alors qu'une valeur par défaut, l'écrire effacerait un
 *   « oui » donné auparavant.
 */
export function newsletterConsentWrite({
    checked,
    touched,
    saved,
}: {
    checked: boolean;
    touched: boolean;
    saved: SavedConsent;
}): { value: boolean | undefined; merge: boolean } {
    if (typeof saved === 'boolean') return { value: touched ? checked : saved, merge: false };
    return { value: touched ? checked : undefined, merge: true };
}
